import { createElement, forwardRef, type FC, type HTMLAttributes, type ReactNode } from 'react'
import styles from './list.module.css'

export interface ListProps extends HTMLAttributes<HTMLUListElement> {
  dense?: boolean
  disablePadding?: boolean
}

export const List = forwardRef<HTMLUListElement, ListProps>(
  ({ dense, disablePadding, className, style, ...rest }, ref) => {
    const classes = [
      styles.list,
      dense ? styles.dense : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <ul
        ref={ref}
        className={classes}
        style={disablePadding ? { ...style, padding: 0 } : style}
        {...rest}
      />
    )
  }
)

List.displayName = 'List'

export interface ListItemProps extends HTMLAttributes<HTMLLIElement> {
  dense?: boolean
  button?: boolean
  selected?: boolean
  disabled?: boolean
  disablePadding?: boolean
  disableGutters?: boolean
  secondaryAction?: ReactNode
  alignItems?: 'flex-start' | 'center'
  /** @deprecated Ignored - kept for MUI compat */
  autoFocus?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  onClose?: () => void
  /** @deprecated Ignored - kept for MUI compat */
  component?: string
}

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(
  ({ dense, button, selected, disabled, disablePadding, disableGutters, secondaryAction, alignItems, autoFocus: _autoFocus, onClose: _onClose, component: _component, className, style, ...rest }, ref) => {
    const classes = [
      styles.item,
      dense ? styles.itemDense : undefined,
      button ? styles.itemButton : undefined,
      selected ? styles.itemSelected : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const computedStyle = {
      ...style,
      ...(disablePadding ? { padding: 0 } : undefined),
      ...(disableGutters ? { paddingLeft: 0, paddingRight: 0 } : undefined),
      ...(disabled ? { opacity: 0.38, pointerEvents: 'none' as const } : undefined),
      ...(secondaryAction ? { paddingRight: 48, position: 'relative' as const } : undefined),
      ...(alignItems ? { alignItems } : undefined),
    }

    return (
      <li ref={ref} className={classes} style={computedStyle} {...rest}>
        {rest.children}
        {secondaryAction ? <div className={styles.itemSecondaryAction}>{secondaryAction}</div> : null}
      </li>
    )
  }
)

ListItem.displayName = 'ListItem'

export interface ListItemIconProps extends HTMLAttributes<HTMLDivElement> {}

export const ListItemIcon: FC<ListItemIconProps> = ({ className, ...rest }) => (
  <div className={[styles.itemIcon, className].filter(Boolean).join(' ')} {...rest} />
)

export interface ListItemAvatarProps extends HTMLAttributes<HTMLDivElement> {}

export const ListItemAvatar: FC<ListItemAvatarProps> = ({ className, ...rest }) => (
  <div className={[styles.itemAvatar, className].filter(Boolean).join(' ')} {...rest} />
)

export interface ListItemTextProps extends HTMLAttributes<HTMLDivElement> {
  primary?: ReactNode
  secondary?: ReactNode
  /** Add left padding to align with items that have an icon */
  inset?: boolean
  /** Truncate primary text */
  primaryTypographyProps?: { noWrap?: boolean; style?: React.CSSProperties; fontSize?: number | string }
  secondaryTypographyProps?: { style?: React.CSSProperties }
}

export const ListItemText: FC<ListItemTextProps> = ({
  primary,
  secondary,
  inset,
  primaryTypographyProps,
  secondaryTypographyProps,
  className,
  children,
  style,
  ...rest
}) => {
  const primaryStyle = {
    ...(primaryTypographyProps?.noWrap ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const } : undefined),
    ...(primaryTypographyProps?.fontSize ? { fontSize: primaryTypographyProps.fontSize } : undefined),
    ...primaryTypographyProps?.style,
  }
  return (
    <div className={[styles.itemText, className].filter(Boolean).join(' ')} style={inset ? { ...style, paddingLeft: 40 } : style} {...rest}>
      {primary != null ? (
        <span
          className={styles.itemTextPrimary}
          style={Object.keys(primaryStyle).length ? primaryStyle : undefined}
        >
          {primary}
        </span>
      ) : null}
      {secondary != null ? (
        <span className={styles.itemTextSecondary} style={secondaryTypographyProps?.style}>
          {secondary}
        </span>
      ) : null}
      {children}
    </div>
  )
}

export interface ListItemSecondaryActionProps extends HTMLAttributes<HTMLDivElement> {}

export const ListItemSecondaryAction: FC<ListItemSecondaryActionProps> = ({ className, ...rest }) => (
  <div className={[styles.itemSecondaryAction, className].filter(Boolean).join(' ')} {...rest} />
)

export interface ListItemButtonProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  disabled?: boolean
  dense?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  disableRipple?: boolean
}

export const ListItemButton = forwardRef<HTMLDivElement, ListItemButtonProps>(
  ({ selected, disabled, dense, disableRipple: _disableRipple, className, onClick, onKeyDown, style, ...rest }, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.currentTarget.click()
      }
      onKeyDown?.(e)
    }
    const classes = [
      styles.item,
      styles.itemButton,
      selected ? styles.itemSelected : undefined,
      dense ? styles.itemDense : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={classes}
        aria-selected={selected || undefined}
        aria-disabled={disabled || undefined}
        style={disabled ? { ...style, opacity: 0.38, pointerEvents: 'none', position: 'relative' } : { ...style, position: 'relative' }}
        onClick={disabled ? undefined : onClick}
        onKeyDown={disabled ? undefined : handleKeyDown}
        {...rest}
      />
    )
  }
)

ListItemButton.displayName = 'ListItemButton'

/** MenuList -- an alias for List used in menu contexts */
export interface MenuListProps extends HTMLAttributes<HTMLUListElement> {
  dense?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  component?: string
  /** @deprecated Ignored - kept for MUI compat */
  autoFocusItem?: boolean
}

export const MenuList = forwardRef<HTMLElement, MenuListProps>(
  ({ dense, component = 'ul', autoFocusItem: _autoFocusItem, className, ...rest }, ref) => {
    const classes = [
      styles.list,
      dense ? styles.dense : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return createElement(component, { ref, className: classes, role: 'menu', ...rest })
  }
)

MenuList.displayName = 'MenuList'
